import {
  Container,
  TripStatus,
  BadgeVariant,
  ImportExportContainer,
  ImportContainerDetail,
  ExportContainerDetail,
} from '../types/container'
import { API_ENDPOINTS } from '../config/api'

// ============================================================================
// API METHODS
// ============================================================================

/**
 * Fetch all containers from the API
 */
export const getContainers = async (): Promise<Container[]> => {
  try {
    // Fetch all trips and map to Container objects for the container views
    const response = await fetch(`${API_ENDPOINTS.trips.list}?page=1&limit=5000`)
    if (!response.ok) {
      throw new Error(`Failed to fetch trips: ${response.statusText}`)
    }
    const data = await response.json()
    const trips = data.data || []

    // Map each trip with a container number to a Container object
    return trips
      .filter((t: any) => t.containerNr1)
      .map((t: any) => ({
        id: t.ritNr,
        number: t.containerNr1 || '',
        type: t.containerType || '',
        shippingLine: t.rederij1 || '',
        status: t.uitvoeringsDatum ? 'delivered'
          : t.voorladen === 'Voorgeladen' ? 'on_yard'
          : 'expected',
        currentLocation: undefined,
        trips: [],
      }))
  } catch (error) {
    console.error('Failed to fetch containers from API:', error)
    return []
  }
}

/**
 * Fetch expected containers from the API
 */
export const getExpectedContainers = async (): Promise<Container[]> => {
  try {
    const response = await fetch(API_ENDPOINTS.containers.expected)
    if (!response.ok) {
      throw new Error(`Failed to fetch expected containers: ${response.statusText}`)
    }
    const data = await response.json()
    return data.containers || []
  } catch (error) {
    console.error('Failed to fetch expected containers from API:', error)
    return []
  }
}

/**
 * Fetch on-yard containers from the API
 */
export const getOnYardContainers = async (): Promise<Container[]> => {
  try {
    const response = await fetch(API_ENDPOINTS.containers.onYard)
    if (!response.ok) {
      throw new Error(`Failed to fetch on-yard containers: ${response.statusText}`)
    }
    const data = await response.json()
    return data.containers || []
  } catch (error) {
    console.error('Failed to fetch on-yard containers from API:', error)
    return []
  }
}

/**
 * Get all trips (placeholder for future API implementation)
 */
export const getTrips = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.trips.list)
    if (!response.ok) {
      throw new Error(`Failed to fetch trips: ${response.statusText}`)
    }
    const data = await response.json()
    return data.trips || []
  } catch (error) {
    console.error('Failed to fetch trips from API:', error)
    return []
  }
}

// ============================================================================
// IMPORT / EXPORT API METHODS
// ============================================================================

function mapTripToImportExport(trip: any): ImportExportContainer {
  return {
    ritNr: trip.ritNr,
    containerNumber: trip.containerNr1 || '',
    containerType: trip.containerType || '',
    shippingLine: trip.rederij1 || '',
    volLeeg: trip.ritType === 'LADEN' ? 'Vol' : trip.ritType === 'LOSSEN' ? 'Leeg' : '',
    chauffeur: trip.uitvoerenBcode || trip.voorladenBcode || '',
  }
}

function mapTripToDetail(trip: any) {
  return {
    ritNr: trip.ritNr,
    containerNumber: trip.containerNr1 || '',
    laadLosLocatie: trip.laadLosLocatie || '',
    datum: trip.datum || '',
    uur: trip.uur || '',
    kaai: trip.afhaalLocatie || '',
    klantCode: trip.klantCode || '',
    klantReferentie: trip.klantReferentie || '',
    afleverLocatie: trip.afleverLocatie || '',
    afleverReferentie: trip.afleverReferentie || '',
    voorladen: trip.voorladen || '',
    uitvoeren: trip.uitvoeren || '',
    afzetten: trip.afzetten || '',
    containerType: trip.containerType || '',
    rederij: trip.rederij1 || '',
    opm: trip.opm || '',
    activities: [],
  }
}

export const getImportContainers = async (): Promise<ImportExportContainer[]> => {
  try {
    // Import = LOSSEN, depot containers where second-to-last activity = STI (15)
    const response = await fetch(`${API_ENDPOINTS.depot.inStock}?limit=500`)
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`)
    const result = await response.json()
    return (result.data || [])
      .filter((t: any) => t.ritType === 'LOSSEN')
      .map(mapTripToImportExport)
  } catch (error) {
    console.error('Failed to fetch import containers:', error)
    return []
  }
}

export const getExportContainers = async (): Promise<ImportExportContainer[]> => {
  try {
    // Export = LADEN, depot containers where second-to-last activity = STI (15)
    const response = await fetch(`${API_ENDPOINTS.depot.inStock}?limit=500`)
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`)
    const result = await response.json()
    return (result.data || [])
      .filter((t: any) => t.ritType === 'LADEN')
      .map(mapTripToImportExport)
  } catch (error) {
    console.error('Failed to fetch export containers:', error)
    return []
  }
}

export const getImportContainerDetail = async (ritNr: string): Promise<ImportContainerDetail | null> => {
  try {
    const response = await fetch(API_ENDPOINTS.trips.byRitNr(ritNr))
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`)
    const result = await response.json()
    return mapTripToDetail(result) as ImportContainerDetail
  } catch (error) {
    console.error('Failed to fetch import detail:', error)
    return null
  }
}

export const getExportContainerDetail = async (ritNr: string): Promise<ExportContainerDetail | null> => {
  try {
    const response = await fetch(API_ENDPOINTS.trips.byRitNr(ritNr))
    if (!response.ok) throw new Error(`Failed: ${response.statusText}`)
    const result = await response.json()
    return mapTripToDetail(result) as ExportContainerDetail
  } catch (error) {
    console.error('Failed to fetch export detail:', error)
    return null
  }
}

// ============================================================================
// BUSINESS LOGIC - PURE FUNCTIONS
// ============================================================================

/**
 * Map trip status to badge variant for UI display.
 * This encapsulates the presentation logic for status badges.
 *
 * @param status - The trip status
 * @returns The appropriate badge variant
 */
export const mapTripStatusToBadgeVariant = (status: TripStatus): BadgeVariant => {
  switch (status) {
    case TripStatus.PLANNED:
      return 'info'

    case TripStatus.IN_PROGRESS:
      return 'warning'

    case TripStatus.COMPLETED:
      return 'success'

    case TripStatus.CANCELLED:
      return 'error'

    default:
      return 'info'
  }
}
